import React, { useState, useEffect } from 'react';
import { FaFolderPlus, FaChevronRight, FaChevronDown, FaEdit, FaTrash, FaFileAlt, FaFile } from 'react-icons/fa';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Bold,
  Italic,
  Underline,
  Link,
  List,
  Alignment,
  Font,
  Heading,
  Table,
  TableToolbar,
  Image,
  ImageToolbar,
  ImageResize,
  ImageUpload,
  FileRepository
} from 'ckeditor5';

import 'ckeditor5/ckeditor5.css';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { getCommunicationTemplates, createCommunicationTemplate, updateCommunicationTemplate, deleteCommunicationTemplate } from '../../api/communicationTemplates';

// Custom upload adapter for CKEditor - converts images to base64 data URLs
class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }

  async upload() {
    const file = await this.loader.file;
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        resolve({ default: reader.result });
      };
      reader.onerror = error => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }

  abort() {
    // Abort upload if needed
  }
}

function MyCustomUploadAdapterPlugin(editor) {
  editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

const EmailTemplates = () => {
  // Company selection logic
  let companyOptions = [];
  const companiesStr = localStorage.getItem('companies');
  if (companiesStr) {
    try {
      const companies = JSON.parse(companiesStr);
      if (Array.isArray(companies) && companies.length > 0) {
        companyOptions = [
          ...companies.map(c => ({
            value: c.id,
            label: c.company_name
          }))
        ];
      }
    } catch (e) {
      companyOptions = [];
    }
  }
  const [selectedCompany, setSelectedCompany] = useState('');

  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [showCreateTemplate, setShowCreateTemplate] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateContent, setTemplateContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [errors, setErrors] = useState({});
  const [pdfAttachments, setPdfAttachments] = useState([]);
  const [pdfError, setPdfError] = useState('');
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Log pdfAttachments changes for debugging
  useEffect(() => {
    console.log('pdfAttachments state changed:', pdfAttachments);
  }, [pdfAttachments]);

  // Fetch templates from API on component mount or company change
  useEffect(() => {
    // Only fetch templates if either no company options (not super admin) or company is selected
    if (companyOptions.length === 0 || selectedCompany) {
      fetchTemplates();
    }
  }, [selectedCompany]);

  const fetchTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = { template_type: 'email' };
      // Always pass company_id - either from selectedCompany or empty string
      payload.company_id = selectedCompany ? selectedCompany.value : '';
      const response = await getCommunicationTemplates(payload);
      if (response.code) {
        // Add isOpen property to folders while preserving the hierarchical structure
        const templatesWithState = response.data.map(item => ({
          ...item,
          isOpen: false
        }));
        setFolders(templatesWithState);
      } else {
        setError(response.message || 'Failed to fetch templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error fetching templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleFolder = (folderId) => {
    const toggleRecursive = (items) => {
      return items.map(item => {
        if (item.id === folderId) {
          return { ...item, isOpen: !item.isOpen };
        }
        if (item.subfolders) {
          return { ...item, subfolders: toggleRecursive(item.subfolders) };
        }
        return item;
      });
    };
    setFolders(toggleRecursive(folders));
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      const payload = {
        name: newFolderName,
        type: 'folder',
        template_type: 'email',
        parent_id: selectedFolderId || null,
        created_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await createCommunicationTemplate(payload);

      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
        setNewFolderName('');
        setShowCreateFolder(false);
        setSelectedFolderId(null);
      } else {
        setError(response.message || 'Failed to create folder');
      }
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Error creating folder');
    }
  };

  const createTemplate = async () => {
    if (!newTemplateName.trim()) return;

    try {
      const payload = {
        name: newTemplateName,
        type: 'template',
        template_type: 'email',
        parent_id: selectedFolderId || null,
        created_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await createCommunicationTemplate(payload);

      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
        setNewTemplateName('');
        setShowCreateTemplate(false);
        setSelectedFolderId(null);
      } else {
        setError(response.message || 'Failed to create template');
      }
    } catch (err) {
      console.error('Error creating template:', err);
      setError('Error creating template');
    }
  };

  const deleteItem = async (itemId, isTemplate = false) => {
    try {
      const payload = { template_type: 'email' };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await deleteCommunicationTemplate(itemId, payload);
      if (response.code) {
        // Refresh the templates list
        await fetchTemplates();
      } else {
        setError(response.message || 'Failed to delete item');
      }
    } catch (err) {
      console.error('Error deleting item:', err);
      setError('Error deleting item');
    }
  };

  const editTemplate = (template) => {
    setEditingTemplate(template);
    setTemplateContent(template.content || '');
    setEmailSubject(template.subject || '');
    // Ensure pdf_attachments is always an array
    const attachments = template.pdf_attachments || [];
    console.log('Template pdf_attachments:', template.pdf_attachments);
    console.log('Attachments array:', attachments);
    console.log('Is array:', Array.isArray(attachments));
    
    // Parse if it's a string, otherwise use as-is
    let parsedAttachments = attachments;
    if (typeof attachments === 'string') {
      try {
        parsedAttachments = JSON.parse(attachments);
      } catch (e) {
        console.error('Error parsing pdf_attachments:', e);
        parsedAttachments = [];
      }
    }
    
    setPdfAttachments(Array.isArray(parsedAttachments) ? parsedAttachments : []);
    setErrors({});
  };

  const MAX_PDF_SIZE = 10 * 1024 * 1024; // 10MB

  const isPdfFile = (file) =>
    file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

  const handlePdfUpload = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(file => !isPdfFile(file));
    if (invalidFiles.length > 0) {
      setPdfError('Only PDF attachments are accepted.');
      e.target.value = '';
      return;
    }
    const oversizedFile = files.find(file => file.size > MAX_PDF_SIZE);
    if (oversizedFile) {
      setPdfError(`File "${oversizedFile.name}" exceeds the 10MB size limit (${(oversizedFile.size / (1024 * 1024)).toFixed(2)}MB)`);
      e.target.value = '';
      return;
    }
    setPdfError('');
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        setPdfAttachments(prev => [...prev, {
          filename: file.name,
          content: base64,
          type: 'application/pdf'
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const removePdfAttachment = (index) => {
    setPdfAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const saveTemplate = async () => {
    const newErrors = {};

    if (!emailSubject.trim()) {
      newErrors.emailSubject = 'Email Subject is required';
    }

    if (!templateContent.trim()) {
      newErrors.templateContent = 'Email Template content is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSavingTemplate(true);
    try {
      const payload = {
        subject: emailSubject,
        content: templateContent,
        pdf_attachments: pdfAttachments,
        updated_by: 1 // This should come from auth context
      };
      if (selectedCompany) {
        payload.company_id = selectedCompany.value;
      }
      const response = await updateCommunicationTemplate(editingTemplate.id, payload);

      if (response.code) {
        toast.success(response.message || 'Template saved successfully');
        // Close modal and clear form, then refresh the templates list
        setEditingTemplate(null);
        setTemplateContent('');
        setEmailSubject('');
        setPdfAttachments([]);
        setErrors({});
        await fetchTemplates();
      } else {
        setError(response.message || 'Failed to save template');
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Error saving template');
    } finally {
      setSavingTemplate(false);
    }
  };

  const renderFolderTree = (items, level = 0) => {
    return items.map(item => {
      const paddingLeft = level * 50;
      const isSubfolder = level > 0;
      const hasChildren = item.subfolders && item.subfolders.length > 0;

      return (
        <div key={item.id} className="folder-item">
          <div
            className="d-flex align-items-center py-2 px-2 hover-bg-light rounded cursor-pointer"
            style={{ paddingLeft: `${paddingLeft + 8}px` }}
          >
            <button
              onClick={() => toggleFolder(item.id)}
              className="btn btn-sm btn-link mr-2 p-1"
            >
              {hasChildren ? (
                item.isOpen ? <FaChevronDown size={16} /> : <FaChevronRight size={16} />
              ) : (
                <div style={{ width: 16 }} />
              )}
            </button>
            <FaFolderPlus size={isSubfolder ? 15 : 25} className="text-primary mr-3" />
            <span
              className="flex-grow-1"
              style={{
                fontWeight: isSubfolder ? 'normal' : 'bold',
                fontSize: isSubfolder ? '14px' : '16px',
                marginLeft:'10px'
              }}
            >
              {item.name}
            </span>
            <div className="d-flex gap-2">
              {item.type === 'folder' && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderId(item.id);
                      setShowCreateFolder(true);
                    }}
                    className="btn btn-sm btn-link p-1"
                    title="Add Subfolder"
                  >
                    <FaFolderPlus size={16} className="text-secondary" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFolderId(item.id);
                      setShowCreateTemplate(true);
                    }}
                    className="btn btn-sm btn-link p-1"
                    title="Add Template"
                  >
                    <FaFile size={16} className="text-secondary" />
                  </button>
                </>
              )}
              {item.type === 'template' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    editTemplate(item);
                  }}
                  className="btn btn-sm btn-link p-1"
                  title="Edit"
                >
                  <FaEdit size={16} className="text-secondary" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  deleteItem(item.id, item.type === 'template');
                }}
                className="btn btn-sm btn-link p-1 text-danger"
                title="Delete"
              >
                <FaTrash size={16} />
              </button>
            </div>
          </div>

          {item.isOpen && hasChildren && (
            <div className="subfolders">
              {renderFolderTree(item.subfolders, level + 1)}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <article className="col-lg-9 articleClass">
      <div className="article-body store-bd-gy">
        <div className="row align-items-center mb-4">
          <div className="col-sm-12">
            <h3 className="page-title">Email Templates</h3>
            <p className="text-muted">Manage your email templates in folders and subfolders</p>
          </div>
        </div>

        {/* Company Selection - Only show if there are company options */}
        {companyOptions.length > 0 && (
          <div className="row mb-4">
            <div className="col-md-6">
              <label className="form-label">Select Company</label>
              <Select
                options={companyOptions}
                value={selectedCompany}
                onChange={(selected) => {
                  setSelectedCompany(selected);
                }}
                placeholder="Select Company"
              />
            </div>
          </div>
        )}

        {/* Show company selection message if no company is selected */}
        {companyOptions.length > 0 && !selectedCompany && (
          <div className="alert alert-info mb-4">
            Please select a company to view and edit email templates.
          </div>
        )}

        <div className="card">
          <div className="card-body">
          {error && (
            <div className="alert alert-danger mb-3">
              {error}
              <button type="button" className="btn-close float-end" onClick={() => setError(null)}></button>
            </div>
          )}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="card-title mb-0">Template Structure</h5>
            <div className="d-flex gap-2">
              <button
                onClick={() => {
                  setSelectedFolderId(null);
                  setShowCreateFolder(true);
                }}
                className="btn btn-primary d-flex align-items-center gap-2"
              >
                <FaFolderPlus size={16} />
                New Folder
              </button>
              <button
                onClick={() => {
                  setSelectedFolderId(null);
                  setShowCreateTemplate(true);
                }}
                className="d-none btn btn-success d-flex align-items-center gap-2"
              >
                <FaFile size={16} />
                New Template
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : folders.length === 0 ? (
            <div className="text-center py-12 text-muted">
              No folders yet. Create a folder to get started.
            </div>
          ) : (
            <div className="folder-tree">
              {renderFolderTree(folders)}
            </div>
          )}
        </div>
      </div>

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedFolderId ? 'Create Subfolder' : 'Create Folder'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                    setSelectedFolderId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name"
                  className="form-control"
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateFolder(false);
                    setNewFolderName('');
                    setSelectedFolderId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={createFolder}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Template Modal */}
      {showCreateTemplate && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {selectedFolderId ? 'Create Template in Folder' : 'Create Template'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowCreateTemplate(false);
                    setNewTemplateName('');
                    setSelectedFolderId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="form-control"
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCreateTemplate(false);
                    setNewTemplateName('');
                    setSelectedFolderId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={createTemplate}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal with Text Editor */}
      {editingTemplate && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content" style={{ height: '90vh' }}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Template: {editingTemplate.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateContent('');
                    setEmailSubject('');
                    setErrors({});
                  }}
                ></button>
              </div>
              <div className="modal-body d-flex flex-column" style={{ flex: 1, overflowY: 'auto' }}>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    Email Subject <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className={`form-control ${errors.emailSubject ? 'is-invalid' : ''}`}
                    placeholder="Enter email subject"
                  />
                  {errors.emailSubject && (
                    <div className="invalid-feedback">
                      {errors.emailSubject}
                    </div>
                  )}
                </div>
                <div className="mb-3 flex-grow-1 d-flex flex-column">
                  <label className="form-label fw-bold">
                    Email Template <span className="text-danger">*</span>
                  </label>
                  {errors.templateContent && (
                    <div className="text-danger mb-2">
                      {errors.templateContent}
                    </div>
                  )}
                  <div className="flex-grow-1" style={{ minHeight: '500px' }}>
                    <CKEditor
                      editor={ClassicEditor}
                      config={{
                        licenseKey: 'GPL',
                        plugins: [
                          Essentials,
                          Paragraph,
                          Bold,
                          Italic,
                          Underline,
                          Link,
                          List,
                          Alignment,
                          Font,
                          Heading,
                          Table,
                          TableToolbar,
                          Image,
                          ImageToolbar,
                          ImageResize,
                          ImageUpload,
                          MyCustomUploadAdapterPlugin
                        ],
                        toolbar: [
                          'undo', 'redo', '|',
                          'heading', '|',
                          'bold', 'italic', 'underline', '|',
                          'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                          'alignment:left', 'alignment:center', 'alignment:right', 'alignment:justify', '|',
                          'link', 'uploadImage', 'insertTable', '|',
                          'bulletedList', 'numberedList', 'outdent', 'indent', '|',
                          'removeFormat'
                        ],
                        heading: {
                          options: [
                            { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                            { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                            { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                            { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                          ]
                        },
                        fontSize: {
                          options: [
                            10, 12, 14, 'default', 18, 20, 24, 28, 32, 36, 48
                          ]
                        },
                        fontFamily: {
                          options: [
                            'default',
                            'Arial, Helvetica, sans-serif',
                            'Courier New, Courier, monospace',
                            'Georgia, serif',
                            'Lucida Sans Unicode, Lucida Grande, sans-serif',
                            'Tahoma, Geneva, sans-serif',
                            'Times New Roman, Times, serif',
                            'Trebuchet MS, Helvetica, sans-serif',
                            'Verdana, Geneva, sans-serif'
                          ]
                        },
                        image: {
                          toolbar: [
                            'imageTextAlternative', '|',
                            'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', 'imageStyle:side', '|',
                            'resizeImage', '|',
                            'toggleImageCaption', '|',
                            'imageRemove'
                          ],
                          styles: [
                            'inline',
                            'alignLeft',
                            'alignCenter',
                            'alignRight',
                            'wrapText',
                            'breakText',
                            'side'
                          ],
                          resizeUnit: 'px'
                        },
                        table: {
                          contentToolbar: [
                            'tableColumn', 'tableRow', 'mergeTableCells'
                          ]
                        }
                      }}
                      data={templateContent}
                      onReady={editor => {
                        editor.editing.view.change(writer => {
                          writer.setStyle('min-height', '500px', editor.editing.view.document.getRoot());
                        });
                      }}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setTemplateContent(data);
                      }}
                    />
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold">
                    PDF Attachments
                  </label>
                  <input
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfUpload}
                    className="form-control"
                    multiple
                  />
                  <small className="text-muted">Only PDF attachments are accepted. Multiple PDF files are allowed.</small>
                  {pdfError && (
                    <div className="text-danger small mt-1">
                      {pdfError}
                    </div>
                  )}
                  {pdfAttachments.length > 0 && (
                    <div className="mt-2">
                      <strong>Attached PDFs:</strong>
                      <ul className="list-group mt-1">
                        {pdfAttachments.map((pdf, index) => (
                          <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center gap-2">
                              <a
                                href={`data:application/pdf;base64,${pdf.content}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-decoration-none"
                                onClick={() => console.log('PDF content length:', pdf.content?.length, 'Filename:', pdf.filename)}
                              >
                                <i className="fa fa-file-pdf me-2" style={{ color: '#dc3545' }}></i>
                                {pdf.filename}
                              </a>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = `data:application/pdf;base64,${pdf.content}`;
                                  link.download = pdf.filename;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                              >
                                <i className="fa fa-download"></i>
                              </button>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-danger"
                              onClick={() => removePdfAttachment(index)}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setEditingTemplate(null);
                    setTemplateContent('');
                    setEmailSubject('');
                    setPdfAttachments([]);
                    setErrors({});
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={saveTemplate}
                  disabled={savingTemplate}
                >
                  {savingTemplate ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </article>
  );
};

export default EmailTemplates;
